const clamp01 = (value: number) => Math.min(1, Math.max(0, value))

const clamp255 = (value: number) => Math.min(255, Math.max(0, value))

const toHexByte = (value: number) =>
  clamp255(Math.round(value)).toString(16).padStart(2, "0")

const toHexColor = (r: number, g: number, b: number) =>
  `#${toHexByte(r)}${toHexByte(g)}${toHexByte(b)}`.toUpperCase()

export const normalizeHexColor = (value: string): string | null => {
  const hex = value.trim().replace(/^#/, "")
  if (/^[\da-f]{3}$/i.test(hex)) {
    return `#${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`.toUpperCase()
  }
  if (/^[\da-f]{6}$/i.test(hex)) {
    return `#${hex}`.toUpperCase()
  }
  if (/^[\da-f]{8}$/i.test(hex)) {
    return `#${hex.slice(0, 6)}`.toUpperCase()
  }
  return null
}

const parseRgbFunctionColor = (value: string): string | null => {
  const normalized = value.trim().toLowerCase()
  if (!normalized.startsWith("rgb(") && !normalized.startsWith("rgba(")) {
    return null
  }

  const bodyMatch = /^[^(]+\((.*)\)$/.exec(normalized)
  if (!bodyMatch) {
    return null
  }

  const tokens = bodyMatch[1]
    .replace("/", " ")
    .split(/[,\s]+/)
    .map((token) => token.trim())
    .filter(Boolean)

  if (tokens.length < 3) {
    return null
  }

  const rgb = tokens.slice(0, 3).map((token) => {
    if (token.endsWith("%")) {
      const percent = Number(token.slice(0, -1))
      return Number.isFinite(percent) ? (percent / 100) * 255 : NaN
    }
    return Number(token)
  })

  if (rgb.some((channel) => !Number.isFinite(channel))) {
    return null
  }

  return toHexColor(rgb[0], rgb[1], rgb[2])
}

const parseOklchColor = (value: string): string | null => {
  const match =
    /^oklch\(\s*([+-]?(?:\d+\.?\d*|\.\d+))(%)?\s+([+-]?(?:\d+\.?\d*|\.\d+))\s+([+-]?(?:\d+\.?\d*|\.\d+))(deg|rad|grad|turn)?(?:\s*\/\s*[^\)]*)?\)$/i.exec(
      value.trim(),
    )
  if (!match) {
    return null
  }

  const [, lRaw, lUnit, cRaw, hRaw, hUnit] = match
  const l = Number(lRaw)
  const c = Number(cRaw)
  const h = Number(hRaw)

  if (!Number.isFinite(l) || !Number.isFinite(c) || !Number.isFinite(h)) {
    return null
  }

  const lightness = lUnit === "%" ? l / 100 : l
  let hueDegrees = h
  if (hUnit === "rad") {
    hueDegrees = (h * 180) / Math.PI
  } else if (hUnit === "grad") {
    hueDegrees = h * 0.9
  } else if (hUnit === "turn") {
    hueDegrees = h * 360
  }

  const hueRadians = (hueDegrees * Math.PI) / 180
  const a = c * Math.cos(hueRadians)
  const b = c * Math.sin(hueRadians)

  const lPrime = lightness + 0.3963377774 * a + 0.2158037573 * b
  const mPrime = lightness - 0.1055613458 * a - 0.0638541728 * b
  const sPrime = lightness - 0.0894841775 * a - 1.291485548 * b

  const lCube = lPrime * lPrime * lPrime
  const mCube = mPrime * mPrime * mPrime
  const sCube = sPrime * sPrime * sPrime

  const linearR = 4.0767416621 * lCube - 3.3077115913 * mCube + 0.2309699292 * sCube
  const linearG = -1.2684380046 * lCube + 2.6097574011 * mCube - 0.3413193965 * sCube
  const linearB = -0.0041960863 * lCube - 0.7034186147 * mCube + 1.707614701 * sCube

  const linearToSrgb = (channel: number) =>
    channel <= 0.0031308
      ? 12.92 * channel
      : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055

  const r = clamp01(linearToSrgb(linearR)) * 255
  const g = clamp01(linearToSrgb(linearG)) * 255
  const b2 = clamp01(linearToSrgb(linearB)) * 255

  return toHexColor(r, g, b2)
}

const parseAngleToDegrees = (value: number, unit?: string): number => {
  if (unit === "rad") {
    return (value * 180) / Math.PI
  }
  if (unit === "grad") {
    return value * 0.9
  }
  if (unit === "turn") {
    return value * 360
  }
  return value
}

const labToHex = (
  lightnessPercent: number,
  a: number,
  b: number,
): string | null => {
  if (
    !Number.isFinite(lightnessPercent) ||
    !Number.isFinite(a) ||
    !Number.isFinite(b)
  ) {
    return null
  }

  const l = Math.min(100, Math.max(0, lightnessPercent))
  const fy = (l + 16) / 116
  const fx = fy + a / 500
  const fz = fy - b / 200

  const delta = 6 / 29
  const fInv = (t: number) =>
    t > delta ? t * t * t : 3 * delta * delta * (t - 4 / 29)

  const xD50 = 0.96422 * fInv(fx)
  const yD50 = 1.0 * fInv(fy)
  const zD50 = 0.82521 * fInv(fz)

  const x = 0.9555766 * xD50 - 0.0230393 * yD50 + 0.0631636 * zD50
  const y = -0.0282895 * xD50 + 1.0099416 * yD50 + 0.0210077 * zD50
  const z = 0.0122982 * xD50 - 0.020483 * yD50 + 1.3299098 * zD50

  const linearR = 3.2404542 * x - 1.5371385 * y - 0.4985314 * z
  const linearG = -0.969266 * x + 1.8760108 * y + 0.041556 * z
  const linearB = 0.0556434 * x - 0.2040259 * y + 1.0572252 * z

  const linearToSrgb = (channel: number) =>
    channel <= 0.0031308
      ? 12.92 * channel
      : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055

  const r = clamp01(linearToSrgb(linearR)) * 255
  const g = clamp01(linearToSrgb(linearG)) * 255
  const b2 = clamp01(linearToSrgb(linearB)) * 255

  return toHexColor(r, g, b2)
}

const parseLabColor = (value: string): string | null => {
  const match =
    /^lab\(\s*([+-]?(?:\d+\.?\d*|\.\d+))%\s+([+-]?(?:\d+\.?\d*|\.\d+))(%)?\s+([+-]?(?:\d+\.?\d*|\.\d+))(%)?(?:\s*\/\s*[^\)]*)?\)$/i.exec(
      value.trim(),
    )
  if (!match) {
    return null
  }

  const [, lRaw, aRaw, aUnit, bRaw, bUnit] = match
  const l = Number(lRaw)
  const a = Number(aRaw)
  const b = Number(bRaw)
  if (!Number.isFinite(l) || !Number.isFinite(a) || !Number.isFinite(b)) {
    return null
  }

  const scaledA = aUnit === "%" ? (a / 100) * 125 : a
  const scaledB = bUnit === "%" ? (b / 100) * 125 : b
  return labToHex(l, scaledA, scaledB)
}

const parseLchColor = (value: string): string | null => {
  const match =
    /^lch\(\s*([+-]?(?:\d+\.?\d*|\.\d+))%\s+([+-]?(?:\d+\.?\d*|\.\d+))(%)?\s+([+-]?(?:\d+\.?\d*|\.\d+))(deg|rad|grad|turn)?(?:\s*\/\s*[^\)]*)?\)$/i.exec(
      value.trim(),
    )
  if (!match) {
    return null
  }

  const [, lRaw, cRaw, cUnit, hRaw, hUnit] = match
  const l = Number(lRaw)
  const c = Number(cRaw)
  const h = Number(hRaw)
  if (!Number.isFinite(l) || !Number.isFinite(c) || !Number.isFinite(h)) {
    return null
  }

  const chroma = cUnit === "%" ? (c / 100) * 150 : c
  const hueDegrees = parseAngleToDegrees(h, hUnit)
  const hueRadians = (hueDegrees * Math.PI) / 180
  const a = chroma * Math.cos(hueRadians)
  const b = chroma * Math.sin(hueRadians)

  return labToHex(l, a, b)
}

const parseBrowserColor = (value: string): string | null => {
  if (typeof document === "undefined") {
    return null
  }

  const probe = document.createElement("span")
  probe.style.color = ""
  probe.style.color = value
  if (!probe.style.color) {
    return null
  }

  probe.style.position = "absolute"
  probe.style.visibility = "hidden"
  probe.style.pointerEvents = "none"
  probe.style.inset = "0"

  const mountNode = document.body ?? document.documentElement
  mountNode.appendChild(probe)
  const resolved = getComputedStyle(probe).color
  probe.remove()

  return parseRgbFunctionColor(resolved)
}

export const toHexFromCssColor = (value: string, fallback: string): string =>
  normalizeHexColor(value) ??
  parseRgbFunctionColor(value) ??
  parseOklchColor(value) ??
  parseLabColor(value) ??
  parseLchColor(value) ??
  parseBrowserColor(value) ??
  fallback

export const resolveThemeColorVarHex = (
  variableName: string,
  fallback: string,
): string => {
  if (typeof document === "undefined") {
    return fallback
  }
  const styles = getComputedStyle(document.documentElement)
  const value = styles.getPropertyValue(variableName).trim()
  if (!value) {
    return fallback
  }
  return toHexFromCssColor(value, fallback)
}

export const hexColorToRgba = (
  hexColor: string,
  alpha: number,
  fallback: string,
): string => {
  const normalized = normalizeHexColor(hexColor)
  if (!normalized) {
    return fallback
  }

  const r = Number.parseInt(normalized.slice(1, 3), 16)
  const g = Number.parseInt(normalized.slice(3, 5), 16)
  const b = Number.parseInt(normalized.slice(5, 7), 16)
  const safeAlpha = Math.max(0, Math.min(1, alpha))
  return `rgba(${r}, ${g}, ${b}, ${safeAlpha})`
}
